import cx from 'classnames'
import { Button } from '@/components/Button'
import { Icon } from '@/components/Icon'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetHeaderDescription,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { markdownToHtml } from '@/utils/markdown-util'
import styles from './ProgressionStepsDialog.module.css'

export interface ProgressionStepsDialogStep {
  id: string
  title: string
  description: string
  state: 'done' | 'current' | 'upcoming'
  currentLabel: string | null
  meta: string | null
}

export interface ProgressionStepsDialogProps {
  showing: boolean
  steps: ProgressionStepsDialogStep[]
  description: string
  hasHistory: boolean
  onClose: () => unknown
  onViewHistory: () => unknown
}

/**
 * Every step of the job's flow with what it asks of the viewer, and when and
 * by whom the job reached the steps behind it.
 */
export function ProgressionStepsDialog({
  showing,
  steps,
  description,
  hasHistory,
  onClose,
  onViewHistory,
}: ProgressionStepsDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={onClose}
      testId="job-progression-steps-dialog"
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Progression</ModalSheetTitle>
        <ModalSheetHeaderDescription>{description}</ModalSheetHeaderDescription>
      </ModalSheetHeader>
      <ModalSheetContent>
        <ol className={styles.steps}>
          {steps.map((step, index) => (
            <Step
              key={step.id}
              step={step}
              number={index + 1}
              isLast={index === steps.length - 1}
            />
          ))}
        </ol>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          {hasHistory && (
            <Button
              label="Full history"
              variant="default"
              onPress={onViewHistory}
              testId="job-progression-view-history"
            />
          )}
          <Button label="Got it" variant="primary" onPress={onClose} />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

interface StepProps {
  step: ProgressionStepsDialogStep
  number: number
  isLast: boolean
}

function Step({ step, number, isLast }: StepProps) {
  return (
    <li className={cx(styles.step, styles[step.state])}>
      <div className={styles.track}>
        <span className={styles.marker}>
          {step.state === 'done' ? (
            <Icon name="tick" size={11} aria-hidden="true" />
          ) : (
            number
          )}
        </span>
        {!isLast && <span className={styles.line} />}
      </div>
      <div className={styles.body}>
        <div className={styles.head}>
          <span className={styles.name}>{step.title}</span>
          {step.currentLabel && (
            <span className={styles.currentLabel}>{step.currentLabel}</span>
          )}
        </div>
        {step.description && (
          <div
            className={styles.description}
            dangerouslySetInnerHTML={{
              __html: markdownToHtml(step.description),
            }}
          />
        )}
        {step.meta && <span className={styles.meta}>{step.meta}</span>}
      </div>
    </li>
  )
}
